import { useRequest } from 'ahooks'
import { useId } from 'react'
import { Optional } from 'utility-types'

import { DEFAULT_OTP_RETRY_TIMER } from '$constants'

import { toast } from '$app/utilities'

import useTimer from '$actions/useTimer'
import useOTPInput from '$actions/useOTPInput'
import useOTPBlock from '$actions/useOTPBlock'

import { asyncSuccessResolver } from '$app/utilities'
import {
  errorMessageResolver,
  errorStatusResolver,
  successResolver
} from '$services/api/common'

import { FactorHookOptions } from './types'
import { MFAChallengeSmsOTPRequest } from '$services/api/types'

type UseSmsOTPOptions = FactorHookOptions

export const useSmsOTP = ({
  preferred_mfa_token_type = 'ott',
  apiInstance: {
    mfaChallengeSmsOTPRequest,
    mfaChallengeSmsOTPRetry,
    mfaChallengeSmsOTPVerify
  },
  onVerified
}: UseSmsOTPOptions) => {
  const toastID = useId()
  const otpTimer = useTimer(DEFAULT_OTP_RETRY_TIMER)

  const onApiError = (err: unknown) => {
    if (errorStatusResolver(err) === 401) {
      resetActions()
    }
  }

  const challengeReq = useRequest(
    asyncSuccessResolver(mfaChallengeSmsOTPRequest, successResolver),
    { manual: true, onError: onApiError }
  )
  const resendReq = useRequest(
    asyncSuccessResolver(mfaChallengeSmsOTPRetry, successResolver),
    { manual: true, onError: onApiError }
  )
  const verifyReq = useRequest(
    asyncSuccessResolver(mfaChallengeSmsOTPVerify, successResolver),
    {
      manual: true,
      onError: onApiError,
      onSuccess: res => onVerified(res.data.mfa_token)
    }
  )

  const requests = [challengeReq, resendReq, verifyReq]

  const challengeResult = challengeReq.data?.data

  const challenge_id = challengeResult?.challenge_id
  const hint = challengeResult?.hint
  const code_length = challengeResult?.code_length
  const reference_code = challengeResult?.reference_code

  const isAnyLoading = requests.map(({ loading }) => loading).includes(true)
  const disabled = isAnyLoading

  const otpInput = useOTPInput({
    length: code_length,
    disabled,
    onFilled: (code, resetInput) => {
      if (!challenge_id) return

      const { runAsync, cancel } = verifyReq

      cancel()

      toast
        .promise(runAsync({ challenge_id, code }), {
          error: errorMessageResolver,
          loading: 'Verifying..',
          success: 'Verified.',
          id: toastID
        })
        .unwrap()
        .finally(resetInput)
    }
  })

  const otpBlock = useOTPBlock({
    otpInput,
    title: 'OTP Verification',
    description: hint || 'We have sent a code to you.',
    timerActive: otpTimer.active,
    timerSeconds: otpTimer.seconds,
    refCode: reference_code,
    onResend: () => {
      if (!challenge_id) return

      const { runAsync, cancel } = resendReq

      cancel()

      toast
        .promise(runAsync({ challenge_id }), {
          error: errorMessageResolver,
          loading: 'Resending..',
          success: 'Sent',
          id: toastID
        })
        .unwrap()
        .finally(() => {
          otpInput.resetPasswords()
          otpTimer.reset()
        })
    }
  })

  const resetActions = () => {
    requests.forEach(({ cancel, mutate }) => {
      cancel()
      mutate()
    })
    otpInput.resetPasswords()
  }

  const reset = () => {
    resetActions()
    toast.dismiss(toastID)
  }

  const challenge = ({
    type = preferred_mfa_token_type,
    ...rest
  }: Optional<MFAChallengeSmsOTPRequest, 'type'>) => {
    const { runAsync } = challengeReq

    reset()
    otpTimer.reset()

    toast.promise(runAsync({ type, ...rest }), {
      error: errorMessageResolver,
      loading: 'Requesting OTP.',
      success: 'OTP requested.',
      id: toastID
    })
  }

  const reChallenge = challengeReq.refresh

  return {
    isAnyLoading,
    reset,
    otpBlock,
    challenge,
    reChallenge
  }
}

export default useSmsOTP
